import { useCallback, useRef, useState } from 'react';
import { hashKey, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { apiFetch } from '../client';
import type {
  BookmarkDetail, BookmarkListItem, SmartFilterId, SortDirection,
  SortField, SystemCategoryId,
} from 'markcat-domain';
import { BOOKMARK_SUMMARY_QUERY_KEY } from './useCategories';

/** One cursor page exactly as the server returns it. */
export interface BookmarkListResponse {
  items: BookmarkListItem[];
  has_more: boolean;
  next_cursor?: string | null;
  /** Present on the first page only. */
  total_count?: number | null;
}

/** A row's place in the default order, `(date DESC, id DESC)`. */
export type BookmarkKey = Pick<BookmarkListItem, 'id' | 'date'>;

/**
 * The cached window: every page the view has scrolled into, concatenated.
 * `rowsLoaded` and `tail` are client-side (hence camelCase among wire fields).
 */
export interface BookmarkListQueryData extends BookmarkListResponse {
  /** How many rows the server returned for the loaded pages: what makes the
   *  window survive a refetch, which reads that many rows again. Without it an
   *  invalidation (SSE reconcile, any mutation, a refocus past staleTime) would
   *  replace a scrolled window with a fresh first page, and the view's scroll
   *  handler would page back in. Counted in rows, not pages, so a read of the
   *  window can take them in fewer, larger requests than the view paged in. */
  rowsLoaded: number;
  /** The last row the server returned for the loaded pages, where the cursor
   *  resumes: the loaded range ends here. Recorded rather than read off
   *  `items`, because an optimistic delete or an event can remove that row
   *  while the range stays where the server left it. Null when no page
   *  returned a row. */
  tail: BookmarkKey | null;
}

export const BOOKMARKS_QUERY_KEY = ['bookmarks', 'list'] as const;
/** The rows the view asks for at a time as it scrolls. */
const PAGE_SIZE = 200;
/** A read of the whole window asks for the server's page ceiling at a time
 *  (`MAX_BOOKMARK_PAGE_SIZE`, service/routes_bookmarks.py): the same rows in
 *  fewer round trips than the view paged them in. */
const READ_LIMIT = 500;

/**
 * A read's pages can leave the server before a change whose event the client
 * applies while they are in transit; landing them as they are would put the
 * older rows back. So each event is also recorded against every read in
 * flight, and the read applies it to its result before that lands.
 * `serverCount` says whose count the result carries: a window read's is the
 * server's, which may or may not hold the change; a next page keeps the
 * cache's, which the event has already moved. A correction returning null
 * means the result cannot absorb it — its count cannot tell whether it holds
 * the change — and it is read again.
 */
export type ReadCorrection = (
  window: BookmarkListQueryData,
  queryKey: readonly unknown[],
  serverCount: boolean,
) => BookmarkListQueryData | null;

interface ReadInFlight {
  queryKey: readonly unknown[];
  corrections: ReadCorrection[];
  /** An invalidation arrived: anything may have changed after this read left. */
  stale: boolean;
}

const readsInFlight = new WeakMap<QueryClient, Set<ReadInFlight>>();

export function correctReadsInFlight(qc: QueryClient, correct: ReadCorrection): void {
  readsInFlight.get(qc)?.forEach((read) => read.corrections.push(correct));
}

function openRead(qc: QueryClient, queryKey: readonly unknown[]): ReadInFlight {
  const read: ReadInFlight = { queryKey, corrections: [], stale: false };
  let open = readsInFlight.get(qc);
  if (!open) readsInFlight.set(qc, (open = new Set()));
  open.add(read);
  return read;
}

function closeRead(qc: QueryClient, read: ReadInFlight): void {
  readsInFlight.get(qc)?.delete(read);
}

const keyOf = (row: BookmarkListItem | undefined): BookmarkKey | null =>
  row ? { id: row.id, date: row.date } : null;

export interface BookmarkQueryFilters {
  category?: string | null;
  systemFilter?: SystemCategoryId | null;
  smartFilter?: SmartFilterId | null;
  tag?: string | null;
  sortField?: SortField;
  sortDirection?: SortDirection;
}

function bookmarkPath(filters: BookmarkQueryFilters, search?: string): string {
  const qs = new URLSearchParams({
    sort: filters.sortField ?? 'date',
    direction: filters.sortDirection ?? 'desc',
  });
  if (filters.category) qs.set('category', filters.category);
  if (filters.systemFilter) qs.set('system_filter', filters.systemFilter);
  if (filters.smartFilter) qs.set('smart_filter', filters.smartFilter);
  if (filters.tag) qs.set('tag', filters.tag);
  if (search) qs.set('q', search);
  return `/api/bookmarks?${qs}`;
}

function pageUrl(path: string, limit: number, cursor?: string | null): string {
  return `${path}&limit=${limit}${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`;
}

/** Every bookmark-derived cache: list and search windows, details (only an open
 *  drawer's refetches), and the sidebar counts. */
export function invalidateBookmarkData(qc: QueryClient) {
  invalidateWindows(qc, () => true);
  qc.invalidateQueries({ queryKey: ['bookmark'], refetchType: 'active' });
  qc.invalidateQueries({ queryKey: BOOKMARK_SUMMARY_QUERY_KEY, refetchType: 'active' });
}

/**
 * Invalidate the matching list and search windows: the one on screen reads
 * again, the others when shown. A read already in flight may predate the
 * change behind the call, so it is marked to read again rather than cancelled
 * and restarted: a deep window restarted by every invalidation that arrives
 * before it lands would never land. A hidden window's read would otherwise
 * land after the invalidation and clear it.
 */
export function invalidateWindows(qc: QueryClient, matches: (queryKey: readonly unknown[]) => boolean) {
  readsInFlight.get(qc)?.forEach((read) => { if (matches(read.queryKey)) read.stale = true; });
  qc.invalidateQueries(
    { queryKey: ['bookmarks'], predicate: (query) => matches(query.queryKey), refetchType: 'active' },
    { cancelRefetch: false },
  );
}

function appendPage(
  loaded: BookmarkListQueryData,
  page: BookmarkListResponse,
): BookmarkListQueryData {
  const seen = new Set(loaded.items.map((item) => item.id));
  return {
    items: [...loaded.items, ...page.items.filter((item) => !seen.has(item.id))],
    has_more: page.has_more,
    next_cursor: page.next_cursor,
    total_count: loaded.total_count,
    rowsLoaded: loaded.rowsLoaded + page.items.length,
    tail: keyOf(page.items.at(-1)) ?? loaded.tail,
  };
}

/**
 * Read the window from the top until it is as deep as the cached one, at
 * least a page and at most the server's page ceiling per request. The depth is
 * re-read on every hop, so a page the view asks for mid-read extends this read
 * instead of being overwritten by it. Events that arrived meanwhile are
 * applied to the result before it lands; one it cannot absorb sends the read
 * round again. The corrections run on the last page's own continuation and
 * React Query writes the result from the same run of microtasks, so no event —
 * each arrives in a task of its own — falls between the two.
 *
 * An invalidation that arrives mid-read sends it round again from the top,
 * without landing what it read. A second one does not restart it: that pass
 * lands the way an event does, keeping the window's freshness and
 * invalidation, and then goes round again. Invalidations arriving faster than
 * a pass therefore cost one read at a time, and the window takes each result
 * instead of none.
 */
async function fetchWindow(
  qc: QueryClient,
  queryKey: readonly unknown[],
  path: string,
  signal?: AbortSignal,
): Promise<BookmarkListQueryData> {
  const depth = () => qc.getQueryData<BookmarkListQueryData>(queryKey)?.rowsLoaded ?? 0;
  let restarted = false;
  for (;;) {
    const read = openRead(qc, queryKey);
    const restart = () => read.stale && !restarted;
    try {
      const first = await apiFetch<BookmarkListResponse>(
        pageUrl(path, Math.min(Math.max(depth(), PAGE_SIZE), READ_LIMIT)), { signal },
      );
      let loaded: BookmarkListQueryData = { ...first, rowsLoaded: first.items.length, tail: keyOf(first.items.at(-1)) };
      while (loaded.rowsLoaded < depth() && loaded.has_more && loaded.next_cursor && !restart()) {
        const limit = Math.min(depth() - loaded.rowsLoaded, READ_LIMIT);
        loaded = appendPage(loaded, await apiFetch<BookmarkListResponse>(pageUrl(path, limit, loaded.next_cursor), { signal }));
      }
      if (restart()) {
        restarted = true;
        continue;
      }
      const corrected = read.corrections.reduce<BookmarkListQueryData | null>(
        (window, correct) => window && correct(window, queryKey, true),
        loaded,
      );
      if (!corrected) continue;
      if (!read.stale) return corrected;
      qc.getQueryCache().find<BookmarkListQueryData>({ queryKey, exact: true })?.setState({ data: corrected });
    } finally {
      closeRead(qc, read);
    }
  }
}

/** A window's next page: one in flight, or the last one's failure and the
 *  cursor it resumed from. */
type NextPage = { fetching: true } | { fetching: false; cursor: string | null | undefined; error: unknown };

function withNextPage(pages: ReadonlyMap<string, NextPage>, key: string, page?: NextPage) {
  const next = new Map(pages);
  if (page) next.set(key, page);
  else next.delete(key);
  return next;
}

function usePagedBookmarks(queryKey: readonly unknown[], path: string, enabled = true) {
  const qc = useQueryClient();
  const hash = hashKey(queryKey);
  // Kept per window: a new query reuses this hook, and an old query's page in
  // flight must neither block the new one's nor show as its loading.
  const inFlight = useRef(new Set<string>());
  const [nextPages, setNextPages] = useState<ReadonlyMap<string, NextPage>>(() => new Map());
  const query = useQuery({
    queryKey,
    // Every fetch rebuilds the window the view already scrolled to — the cached
    // entry's own depth is the target, so an invalidation refreshes those rows
    // instead of dropping back to the first page.
    queryFn: ({ signal }) => fetchWindow(qc, queryKey, path, signal),
    enabled,
  });

  /** Never rejects: a failed page leaves the loaded rows and records its
   *  error until the next attempt. */
  const fetchNextPage = useCallback(async () => {
    if (inFlight.current.has(hash)) return;
    inFlight.current.add(hash);
    setNextPages((pages) => withNextPage(pages, hash, { fetching: true }));
    let cursor: string | null | undefined;
    let failure: NextPage | undefined;
    try {
      // A page that cannot land is discarded and the fetch re-driven from the
      // window's cursor as it stands then — the view only fires on scroll, so a
      // silent drop would stall the list until the user nudges it. A refetch
      // that landed meanwhile rebuilt the window and moved the tail, so
      // appending against the old cursor would duplicate rows or skip past
      // them; a resync meanwhile may have changed any row the page holds.
      for (;;) {
        const current = qc.getQueryData<BookmarkListQueryData>(queryKey);
        cursor = current?.next_cursor;
        if (!current?.has_more || !cursor) return;
        const read = openRead(qc, queryKey);
        let page: BookmarkListResponse;
        try {
          page = await apiFetch<BookmarkListResponse>(pageUrl(path, PAGE_SIZE, cursor));
        } finally {
          closeRead(qc, read);
        }
        const query = qc.getQueryCache().find<BookmarkListQueryData>({ queryKey, exact: true });
        const old = query?.state.data;
        if (!query || !old || old.next_cursor !== cursor || read.stale) continue;
        // Events in transit already reached `old` and moved its count the
        // way the cache decides; only the new page's rows can predate them.
        const grown = read.corrections.reduce<BookmarkListQueryData | null>(
          (window, correct) => window && correct(window, queryKey, false),
          appendPage(old, page),
        );
        if (!grown) continue;
        // Written like an event: one page is not a read of the window, so the
        // window keeps its freshness and any invalidation a resync left on it.
        query.setState({ data: { ...grown, total_count: old.total_count } });
        return;
      }
    } catch (error) {
      failure = { fetching: false, cursor, error };
    } finally {
      inFlight.current.delete(hash);
      setNextPages((pages) => withNextPage(pages, hash, failure));
    }
  }, [hash, path, qc, queryKey]);

  const nextPage = nextPages.get(hash);
  return {
    ...query,
    hasNextPage: query.data?.has_more ?? false,
    isFetchingNextPage: nextPage?.fetching === true,
    fetchNextPage,
    // A failure describes the page after its cursor: once a read rebuilds the
    // window past it, or ends the list there, that page is no longer missing.
    nextPageError: nextPage && !nextPage.fetching && query.data?.has_more && query.data.next_cursor === nextPage.cursor
      ? nextPage.error
      : null,
    queryKey,
  };
}

/** The main list loads one bounded page and advances only when the view asks.
 *  Pass `enabled: false` while another view (search) is displayed — the cached
 *  window stays readable but broad invalidations no longer rebuild it page by
 *  page for a list nobody sees; it refetches once when re-enabled. */
export function useBookmarks(filters: BookmarkQueryFilters = {}, enabled = true) {
  const path = bookmarkPath(filters);
  const isDefault = !filters.category && !filters.systemFilter && !filters.smartFilter
    && !filters.tag
    && (filters.sortField ?? 'date') === 'date'
    && (filters.sortDirection ?? 'desc') === 'desc';
  return usePagedBookmarks(
    isDefault ? BOOKMARKS_QUERY_KEY : [...BOOKMARKS_QUERY_KEY, path],
    path,
    enabled,
  );
}

/** Server-side full-text search uses the same bounded cursor contract. */
export function useBookmarkSearch(q: string, filters: BookmarkQueryFilters = {}) {
  const path = bookmarkPath(filters, q);
  return usePagedBookmarks(
    ['bookmarks', 'search', path] as const,
    path,
    q.length > 0,
  );
}

export async function fetchMatchingBookmarkIds(
  filters: BookmarkQueryFilters,
  search?: string,
): Promise<string[]> {
  const qs = new URLSearchParams();
  if (filters.category) qs.set('category', filters.category);
  if (filters.systemFilter) qs.set('system_filter', filters.systemFilter);
  if (filters.smartFilter) qs.set('smart_filter', filters.smartFilter);
  if (filters.tag) qs.set('tag', filters.tag);
  if (search) qs.set('q', search);
  const response = await apiFetch<{ ids: string[] }>(`/api/bookmarks/ids?${qs}`);
  return response.ids;
}

export function useBookmark(id: string | null) {
  return useQuery({
    queryKey: ['bookmark', id],
    queryFn: () => apiFetch<BookmarkDetail>(`/api/bookmarks/${id}`),
    enabled: !!id,
  });
}
